import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartAnnotationFlag } from '../ChartAnnotationFlag'
import { ChartFrame } from '../ChartFrame'
import { useChartSeries } from '../ChartSeriesContext'
import { ChartTooltipView } from '../ChartTooltipView'
import {
  axisTickLabels,
  baseKey,
  buildSeriesRows,
  CHART_MARGIN,
  computeXDomain,
  computeYAxisWidth,
  computeYDomain,
  DEFAULT_X_AXIS_HEIGHT,
  fitToGrid,
  makeFormatDateTick,
  TICK_FONT_SIZE,
  useChartChrome,
  X_AXIS_PADDING,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_TICK_MARGIN,
  Y_AXIS_WIDTH,
} from '../cartesianChart'
import { makeYAxisTickFormatter } from '../formatYAxisTick'
import { yTicksForDomain } from '../yAxisTicks'
import { NoDataHatch } from '../NoDataHatch'
import {
  augmentRowsWithNoDataSegments,
  DOT_RADIUS,
  isGreyingEligible,
  LINE_STROKE_WIDTH,
  renderNoDataDotLines,
  renderNoDataGapLines,
} from '../noDataLine'
import {
  pickComparisonXTicks,
  StackedDateTick,
  X_AXIS_HEIGHT_STACKED,
} from '../stackedDateTick'
import { MetricUnit, SummaryMode } from '../types'
import type { ChartAnnotation } from '../types'
import {
  ANNOTATION_AXIS_HEIGHT,
  useChartAnnotations,
} from '../useChartAnnotations'

import { defaultDayTicks } from '../xAxisTicks'
import styles from '../chartAnnotations.module.css'

/** Constant y=0 accessor for the invisible hover-anchor line. */
const hoverAnchorValue = (): number => 0

export interface TimeSeriesChartProps {
  /** Accessible name for the chart (e.g. "Followers over time by channel"). */
  ariaLabel: string
  height?: number
  /** Force all series onto the left Y-axis. Two distinct base metrics otherwise
   *  split across dual axes (e.g. Posts + Followers on content impact). */
  singleAxis?: boolean
  startDate?: Date
  endDate?: Date
  /** Overlay shown when there's no data; the axes/grid stay visible. */
  emptyState?: ReactNode
  /** Show each series' icon in the tooltip (e.g. channel icons). */
  showIconsInTooltip?: boolean
  /** Disable enter animations (used by the offscreen export snapshot). */
  disableAnimations?: boolean
  xAxisTickCount?: number
  /** X-axis shows only the comparison-period dates (Comparison view). */
  comparisonOnly?: boolean
  /** Month-only date labels for monthly-bucketed data. */
  monthly?: boolean
  /** Y-axis label; defaults to the first series' label. */
  yAxisLabel?: string
  xAxisTicks?: number[]
  /**
   * Flag markers pinned to the time axis (see {@link ChartAnnotation}). Pair
   * with day-aligned `xAxisTicks` so date labels line up under the flags.
   */
  annotations?: ChartAnnotation[]
  /** Add an invisible baseline so an all-null leading gap is still hoverable. */
  enableNoDataHover?: boolean
  /** Fixed y-tick count that fills `height` evenly, bypassing the 48px
   *  fit-to-height cadence. The export snapshot sets this so its taller card
   *  shows the same tick count as the on-screen chart instead of many more. */
  yTickCount?: number
}

/**
 * The base time-series line chart: draws 1–N lines from {@link ChartSeriesContext}
 * across one or two Y-axes, with optional dashed comparison overlays.
 * Presentational and style-owning — colour is never a prop.
 *
 * Each cumulative (Latest) line is split (via {@link augmentRowsWithNoDataSegments})
 * into an observed colour line plus a grey no-data line over its null runs; those
 * days read "No data available" in the tooltip. Keyboard-navigable via Recharts
 * `accessibilityLayer`, with a screen-reader summary + data table (via ChartFrame).
 */
export function TimeSeriesChart({
  ariaLabel,
  height = 250,
  singleAxis = false,
  startDate,
  endDate,
  emptyState,
  showIconsInTooltip = false,
  disableAnimations = false,
  xAxisTickCount,
  comparisonOnly = false,
  monthly = false,
  yAxisLabel,
  xAxisTicks,
  annotations = [],
  enableNoDataHover = false,
  yTickCount,
}: TimeSeriesChartProps): JSX.Element {
  const series = useChartSeries()
  const {
    resolvedColors,
    noDataColor,
    surfaceColor,
    gridColor,
    neutralTextColor,
    subtleTextColor,
    cursor,
  } = useChartChrome(series)

  const {
    annotationPoints,
    activeAnnotationId,
    setActiveAnnotationId,
    activeAnnotation,
    showAnnotations,
    closeAnnotationOnGraphHover,
  } = useChartAnnotations(annotations)

  // Group series by base metric: two distinct base metrics → dual axes.
  const baseKeys = useMemo(() => {
    const seen = new Set<string>()
    const result: string[] = []
    for (const s of series) {
      const k = baseKey(s)
      if (!seen.has(k)) {
        seen.add(k)
        result.push(k)
      }
    }
    return result
  }, [series])
  const leftMetrics = useMemo(
    () => series.filter((s) => baseKey(s) === baseKeys[0]),
    [series, baseKeys],
  )
  const rightMetrics = useMemo(
    () =>
      baseKeys.length > 1
        ? series.filter((s) => baseKey(s) === baseKeys[1])
        : [],
    [series, baseKeys],
  )
  const useDualAxes =
    !singleAxis && baseKeys.length === 2 && rightMetrics.length > 0

  // Rows of observed values, before the no-data split. Y-domain is derived from
  // these (the split adds no values outside the observed range).
  const observed = useMemo(() => buildSeriesRows(series), [series])
  const { timestamps, comparisonDateByTimestamp } = observed

  const empty = emptyState != null
  const yDomain = useMemo(
    () =>
      computeYDomain(observed.data, useDualAxes ? leftMetrics : series, {
        column: false,
        stacked: false,
        empty,
      }),
    [observed.data, series, leftMetrics, useDualAxes, empty],
  )
  const yDomainRight = useMemo(
    (): [number, number | string] =>
      useDualAxes
        ? computeYDomain(observed.data, rightMetrics, {
            column: false,
            stacked: false,
            empty,
          })
        : [0, 'auto'],
    [observed.data, rightMetrics, useDualAxes, empty],
  )

  // Each axis has its own floor for the leading-gap ramp — a right-axis series
  // ramps from the right domain's baseline, not the left's.
  const leftFloor = typeof yDomain[0] === 'number' ? yDomain[0] : 0
  const rightFloor = typeof yDomainRight[0] === 'number' ? yDomainRight[0] : 0
  const data = useMemo(
    () =>
      augmentRowsWithNoDataSegments(observed.data, series, {
        leftFloor,
        rightFloor,
        useDualAxes,
        baseKeys,
      }),
    [observed, series, leftFloor, rightFloor, useDualAxes, baseKeys],
  )

  const xDomain = useMemo(
    () => computeXDomain(timestamps, startDate, endDate),
    [timestamps, startDate, endDate],
  )

  const isSinglePoint = data.length <= 1

  // Force ticks at data positions when a comparison overlay needs an aligned
  // second date label; otherwise let recharts auto-pick (or use caller ticks).
  const comparisonXTicks = useMemo(
    () => pickComparisonXTicks(data, comparisonDateByTimestamp, xAxisTickCount),
    [data, comparisonDateByTimestamp, xAxisTickCount],
  )

  const formatDateTick = useMemo(
    () => makeFormatDateTick(startDate, endDate, monthly),
    [startDate, endDate, monthly],
  )

  // Day-aligned default ticks (a subset of the real data timestamps) so each label
  // sits exactly under its data point. Without this recharts auto-picks evenly by
  // value, landing ticks between days so the label drifts off the point + hover dot.
  const defaultXTicks = useMemo(
    () =>
      defaultDayTicks(
        data.map((d) => d.timestamp),
        xAxisTickCount,
      ),
    [data, xAxisTickCount],
  )

  const xTicks = data.map((d) => d.timestamp)
  const explicitXAxisTicks = isSinglePoint
    ? xTicks
    : (comparisonXTicks ?? xAxisTicks ?? defaultXTicks)

  const hasStackedComparisonDates =
    comparisonDateByTimestamp.size > 0 && !comparisonOnly
  const xAxisHeight =
    hasStackedComparisonDates && showAnnotations
      ? Math.max(X_AXIS_HEIGHT_STACKED, ANNOTATION_AXIS_HEIGHT)
      : hasStackedComparisonDates
        ? X_AXIS_HEIGHT_STACKED
        : showAnnotations
          ? ANNOTATION_AXIS_HEIGHT
          : undefined
  const effectiveXAxisHeight = xAxisHeight ?? DEFAULT_X_AXIS_HEIGHT

  // Both Y-axes take the same tick count, so the right-axis labels land on the
  // left-axis gridlines; the snapshot pins the count instead of fitting 48px.
  const { chartHeight, tickCount } =
    yTickCount != null
      ? { chartHeight: height, tickCount: yTickCount }
      : fitToGrid(height, effectiveXAxisHeight)
  const leftAxis = useMemo(
    () =>
      yTicksForDomain(yDomain, tickCount, {
        integer: leftMetrics[0]?.unit !== MetricUnit.PERCENTAGE,
        zeroBaseline: false,
      }),
    [yDomain, tickCount, leftMetrics],
  )
  const rightAxis = useMemo(
    () =>
      yTicksForDomain(yDomainRight, tickCount, {
        integer: rightMetrics[0]?.unit !== MetricUnit.PERCENTAGE,
        zeroBaseline: false,
      }),
    [yDomainRight, tickCount, rightMetrics],
  )

  const leftAxisLabel = yAxisLabel ?? leftMetrics[0]?.label
  const rightAxisLabel = useDualAxes ? rightMetrics[0]?.label : leftAxisLabel

  // Size each band to its own tick labels so the numbers stay 12px from the
  // rotated axis label whatever their width (see computeYAxisWidth).
  const leftAxisWidth = useMemo(
    () =>
      empty
        ? Y_AXIS_WIDTH
        : computeYAxisWidth(
            axisTickLabels(
              leftAxis.ticks,
              makeYAxisTickFormatter(
                leftAxis.domain,
                leftMetrics[0]?.unit,
                tickCount,
              ),
            ),
          ),
    [empty, leftAxis, leftMetrics, tickCount],
  )
  const rightAxisWidth = useMemo(
    () =>
      empty
        ? Y_AXIS_WIDTH
        : computeYAxisWidth(
            axisTickLabels(
              rightAxis.ticks,
              makeYAxisTickFormatter(
                rightAxis.domain,
                rightMetrics[0]?.unit,
                tickCount,
              ),
            ),
          ),
    [empty, rightAxis, rightMetrics, tickCount],
  )

  return (
    <ChartFrame
      ariaLabel={ariaLabel}
      monthly={monthly}
      emptyState={emptyState}
      height={chartHeight}
    >
      <LineChart
        data={data}
        margin={CHART_MARGIN}
        accessibilityLayer
        aria-label={ariaLabel}
        onMouseMove={closeAnnotationOnGraphHover}
      >
        {/* Bind to the left axis: recharts' default yAxisId 0 is a hidden implicit
            axis, so without this the gridlines wouldn't sit on our ticks (dual-axis esp.). */}
        <CartesianGrid yAxisId="left" vertical={false} stroke={gridColor} />

        {/* "No data before" hatch for the active flag — drawn before the series
            so the data lines stay on top. Spans the plot left edge → flag date. */}
        {showAnnotations && activeAnnotation?.showNoDataOverlay && (
          <ReferenceArea
            yAxisId="left"
            x1={xDomain[0]}
            x2={activeAnnotation.ts}
            ifOverflow="hidden"
            shape={
              <NoDataHatch
                color={neutralTextColor}
                className={styles.annotationReveal}
              />
            }
          />
        )}

        <XAxis
          dataKey="timestamp"
          type="number"
          domain={xDomain}
          ticks={explicitXAxisTicks}
          tickFormatter={
            comparisonDateByTimestamp.size > 0 ? undefined : formatDateTick
          }
          tick={
            comparisonDateByTimestamp.size > 0
              ? (props) => (
                  <StackedDateTick
                    {...props}
                    color={neutralTextColor}
                    comparisonDateByTimestamp={comparisonDateByTimestamp}
                    comparisonOnly={comparisonOnly}
                    formatTick={formatDateTick}
                  />
                )
              : { fontSize: TICK_FONT_SIZE, fill: neutralTextColor, dy: 8 }
          }
          height={effectiveXAxisHeight}
          axisLine={{ stroke: gridColor }}
          tickLine={false}
          padding={{ left: X_AXIS_PADDING, right: X_AXIS_PADDING }}
        />

        <YAxis
          yAxisId="left"
          orientation="left"
          domain={leftAxis.domain}
          ticks={leftAxis.ticks}
          width={leftAxisWidth}
          tick={{ fontSize: TICK_FONT_SIZE, fill: subtleTextColor }}
          axisLine={false}
          tickLine={false}
          tickSize={0}
          tickMargin={Y_AXIS_TICK_MARGIN}
          tickFormatter={
            empty
              ? undefined
              : makeYAxisTickFormatter(
                  leftAxis.domain,
                  leftMetrics[0]?.unit,
                  tickCount,
                )
          }
        >
          {leftAxisLabel && (
            <Label
              value={leftAxisLabel}
              angle={-90}
              position="insideLeft"
              offset={Y_AXIS_LABEL_OFFSET}
              style={{
                fill: subtleTextColor,
                fontSize: TICK_FONT_SIZE,
                textAnchor: 'middle',
              }}
            />
          )}
        </YAxis>

        {useDualAxes && (
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={rightAxis.domain}
            ticks={rightAxis.ticks}
            width={rightAxisWidth}
            tick={{ fontSize: TICK_FONT_SIZE, fill: subtleTextColor }}
            axisLine={false}
            tickLine={false}
            tickSize={0}
            tickMargin={Y_AXIS_TICK_MARGIN}
            tickFormatter={
              empty
                ? undefined
                : makeYAxisTickFormatter(
                    rightAxis.domain,
                    rightMetrics[0]?.unit,
                    tickCount,
                  )
            }
          >
            {rightAxisLabel && (
              <Label
                value={rightAxisLabel}
                angle={90}
                position="insideRight"
                offset={Y_AXIS_LABEL_OFFSET}
                style={{
                  fill: subtleTextColor,
                  fontSize: TICK_FONT_SIZE,
                  textAnchor: 'middle',
                }}
              />
            )}
          </YAxis>
        )}

        <Tooltip
          // Suppress the data tooltip (and its cursor line) while an annotation
          // flag's popover is open so the two overlays don't overlap; `undefined`
          // returns hover control to recharts otherwise.
          active={activeAnnotationId != null ? false : undefined}
          content={(props) => (
            <ChartTooltipView
              {...props}
              showIcons={showIconsInTooltip}
              monthly={monthly}
            />
          )}
          cursor={cursor}
        />

        {/* Invisible full-range baseline so an all-null leading gap is still
            hoverable and the tooltip can report "No data available" there. */}
        {enableNoDataHover && (
          <Line
            yAxisId="left"
            dataKey={hoverAnchorValue}
            name="__hoverAnchor"
            stroke="none"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
            legendType="none"
          />
        )}

        {/* Grey no-data lines render before the colour lines so they sit *under*
            them (draw order is z-order) — grey never shows over an observed segment. */}
        {renderNoDataGapLines(series, {
          noDataColor,
          surfaceColor,
          useDualAxes,
          baseKeys,
          disableAnimations,
        })}

        {series.map((s): JSX.Element => {
          const yAxisId =
            useDualAxes && baseKey(s) === baseKeys[1] ? 'right' : 'left'
          return (
            <Line
              key={s.id}
              type="monotone"
              dataKey={s.id}
              yAxisId={yAxisId}
              stroke={resolvedColors[s.id]}
              strokeWidth={LINE_STROKE_WIDTH}
              strokeDasharray={
                s.kind === 'comparison' || s.dashed ? '4 2' : undefined
              }
              // A lone-point chart needs a visible dot; otherwise filled dots come
              // from the `__dot` marker line below (only at real collection points).
              dot={
                isSinglePoint
                  ? {
                      r: DOT_RADIUS,
                      fill: resolvedColors[s.id],
                      strokeWidth: 0,
                    }
                  : false
              }
              activeDot={{ r: DOT_RADIUS }}
              // Eligible series break at nulls — the grey no-data line above draws
              // the gaps. Comparison overlays bridge their own gaps; per-day Totals
              // (e.g. Posts) keep them. The tooltip still reads "No data available".
              connectNulls={
                isGreyingEligible(s)
                  ? false
                  : s.summaryMode !== SummaryMode.TOTAL
              }
              isAnimationActive={!disableAnimations}
            />
          )
        })}

        {/* Filled dots at real collection points that border a gap or stand alone
            (a contiguous daily run gets none), on top of the colour line. */}
        {renderNoDataDotLines(series, {
          resolvedColors,
          useDualAxes,
          baseKeys,
        })}
        {/* Faint guide line at each grouped date of the active flag. Recharts
            forwards `className` onto the line's wrapping <g>, so it fades in on
            open. */}
        {showAnnotations &&
          activeAnnotation?.lineTimestamps.map((ts) => (
            <ReferenceLine
              key={`${activeAnnotation.id}-guide-${ts}`}
              className={styles.annotationReveal}
              yAxisId="left"
              x={ts}
              stroke={neutralTextColor}
              strokeDasharray="3 3"
              strokeOpacity={0.5}
            />
          ))}

        {/* Flag chips. The carrier line is invisible — guide lines are drawn
            separately above so one flag can mark several dates. */}
        {showAnnotations &&
          annotationPoints.map((annotation) => (
            <ReferenceLine
              key={annotation.id}
              yAxisId="left"
              x={annotation.ts}
              stroke="transparent"
              label={
                <ChartAnnotationFlag
                  ariaLabel={annotation.ariaLabel}
                  popoverContent={annotation.content}
                  stacked={hasStackedComparisonDates}
                  open={activeAnnotationId === annotation.id}
                  onOpenChange={(open) =>
                    // Switching flags is order-independent: clicking B fires B's
                    // open and A's close in one click. Only clear if this flag is
                    // still active, so A's close can't clobber B's open.
                    setActiveAnnotationId((current) =>
                      open
                        ? annotation.id
                        : current === annotation.id
                          ? null
                          : current,
                    )
                  }
                />
              }
            />
          ))}
      </LineChart>
    </ChartFrame>
  )
}
